import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Map, { Layer, Source, type MapLayerMouseEvent, type MapRef } from "react-map-gl";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_TOKEN } from "../AssetMapView/utils/mapStyles";
import { readSwatch } from "../../utils/colorUtils";
import type { AssetCard } from "../../utils/operationsDashboard";

// AssetMapView takes no external selection, so this is a lean assets-only map on the same react-map-gl base as MiniMap

const LIGHT_STYLE = "mapbox://styles/mapbox/light-v11";

interface OperationsAssetMapProps {
  cards: AssetCard[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  className?: string;
}

const OperationsAssetMap: React.FC<OperationsAssetMapProps> = ({
  cards,
  selectedId,
  onSelect,
  className = "",
}) => {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const geojson = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: cards.map((card) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [card.lng, card.lat] },
        properties: {
          id: card.id,
          name: card.name,
          // One alarm, one accent, one neutral (the palette has no other status colours); read here, as readSwatch needs the stylesheet
          colour:
            card.severity === "negative"
              ? readSwatch("text-eai-danger")
              : card.severity === "attention"
              ? readSwatch("text-eai-cta-primary")
              : readSwatch("text-eai-ink-faint"),
        },
      })),
    }),
    [cards]
  );

  const fitToCards = useCallback(() => {
    const map = mapRef.current;
    if (!map || geojson.features.length === 0) return;
    const [minLng, minLat, maxLng, maxLat] = turf.bbox(geojson);
    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      // maxZoom stops a single asset from slamming the camera to street level
      { padding: 48, duration: 0, maxZoom: 8 }
    );
  }, [geojson]);

  // Read inside the resize observer, which is set up once and must not close over a stale camera
  const fitRef = useRef(fitToCards);
  const selectedRef = useRef(selectedId);
  useEffect(() => {
    fitRef.current = fitToCards;
    selectedRef.current = selectedId;
  }, [fitToCards, selectedId]);

  // mapbox-gl v1 measures its canvas once, so a map mounted in an unsettled grid cell renders wrong until told to remeasure
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      mapRef.current?.resize();
      // The cell halves when cards open beside it; not while an asset is selected, which would fight the fly-to
      if (!selectedRef.current) fitRef.current();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Card → map. Flying rather than jumping keeps the link between the row clicked and the pin that moves.
  useEffect(() => {
    const map = mapRef.current;
    const target = cards.find((card) => card.id === selectedId);
    if (!map || !target) return;
    map.flyTo({ center: [target.lng, target.lat], zoom: Math.max(map.getZoom(), 7), duration: 700 });
  }, [selectedId, cards]);

  // Re-fit when the filter changes the set, but not while one asset is selected — that would fight the fly
  useEffect(() => {
    if (!selectedId) fitToCards();
  }, [fitToCards, selectedId]);

  const handleClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const id = event.features?.[0]?.properties?.id;
      // Clicking empty water clears the selection, which is how a map says "nothing here"
      onSelect(typeof id === "string" ? id : null);
    },
    [onSelect]
  );

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden rounded-eai-card border border-eai-border ${className}`}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={LIGHT_STYLE}
        initialViewState={{ longitude: -103.037, latitude: 53.51, zoom: 3 }}
        style={{ width: "100%", height: "100%" }}
        interactiveLayerIds={["operations-assets"]}
        onLoad={fitToCards}
        onClick={handleClick}
        attributionControl={false}
        cursor="pointer"
      >
        <Source id="operations-assets-source" type="geojson" data={geojson}>
          <Layer
            id="operations-assets"
            type="circle"
            paint={{
              "circle-radius": ["case", ["==", ["get", "id"], selectedId ?? ""], 9, 6],
              "circle-color": ["get", "colour"],
              "circle-stroke-width": 2,
              "circle-stroke-color": [
                "case",
                ["==", ["get", "id"], selectedId ?? ""],
                // eai-teal-deep, the sidebar active tone the selected card wears
                readSwatch("text-eai-teal-deep"),
                readSwatch("text-eai-surface-card"),
              ],
            }}
          />
        </Source>
      </Map>
    </div>
  );
};

export default OperationsAssetMap;
